import React, { Component } from 'react';
import { func, object, string } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from '../../util/reactIntl';
import { Menu, MenuLabel, MenuContent, MenuItem } from '../../components';
import css from './AgeRangeFilter.module.css';

const AGE_RANGES = [
  { key: '18-22', label: '18-22', minAge: 18, maxAge: 22 },
  { key: '22-30', label: '22-30', minAge: 22, maxAge: 30 },
  { key: '30-40', label: '30-40', minAge: 30, maxAge: 40 },
  { key: '40+', label: '40+', minAge: 40, maxAge: 95 },
];

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Parse value like "1983,2003" (birth years) back to age range key
const parseAgeRange = (ageParam) => {
  if (!ageParam) return null;
  
  const [minBirthYear, maxBirthYear] = ageParam.split(',').map(v => parseInt(v, 10));
  const currentYear = new Date().getFullYear();
  
  // Convert birth years back to ages (reverse the +1/-1 adjustments)
  const maxAge = currentYear - (minBirthYear + 1);
  const minAge = currentYear - (maxBirthYear - 1);
  
  // Find matching range
  for (const range of AGE_RANGES) {
    if (range.minAge === minAge && range.maxAge === maxAge) {
      return range.key;
    }
  }
  return null;
};

// Format age range to birth year format "minBirthYear,maxBirthYear"
const formatAgeRange = (rangeKey, queryParamName) => {
  if (!rangeKey) {
    return { [queryParamName]: null };
  }
  
  const range = AGE_RANGES.find(r => r.key === rangeKey);
  if (!range) {
    return { [queryParamName]: null };
  }
  
  const currentYear = new Date().getFullYear();
  // Add/subtract 1 to make ranges inclusive on both ends
  const minBirthYear = (currentYear - range.maxAge) - 1;
  const maxBirthYear = (currentYear - range.minAge) + 1;
  
  return { [queryParamName]: `${minBirthYear},${maxBirthYear}` };
};

class AgeRangeFilterPopup extends Component {
  constructor(props) {
    super(props);
    this.selectOption = this.selectOption.bind(this);
  }

  selectOption(queryParamName, option) {
    const { onSubmit } = this.props;
    onSubmit(formatAgeRange(option, queryParamName));
  }

  render() {
    const { rootClassName, className, label, queryParamNames, initialValues } = this.props;

    const queryParamName = getQueryParamName(queryParamNames);
    const initialValue = initialValues ? initialValues[queryParamName] : null;
    const selectedRange = parseAgeRange(initialValue);

    const labelText = selectedRange 
      ? `${label}: ${selectedRange}`
      : label;

    const classes = classNames(rootClassName || css.rootPopup, className);

    return (
      <Menu>
        <MenuLabel className={selectedRange ? css.menuLabelSelected : css.menuLabel}>
          {labelText}
        </MenuLabel>
        <MenuContent className={css.menuContent}>
          {AGE_RANGES.map(range => {
            const selected = selectedRange === range.key;
            const menuItemBorderClass = selected ? css.menuItemBorderSelected : css.menuItemBorder;

            return (
              <MenuItem key={range.key}>
                <button
                  className={css.menuItem}
                  onClick={() => this.selectOption(queryParamName, range.key)}
                  type="button"
                >
                  <span className={menuItemBorderClass} />
                  {range.label}
                </button>
              </MenuItem>
            );
          })}
          <MenuItem key="clearLink">
            <button
              className={css.clearMenuItem}
              onClick={() => this.selectOption(queryParamName, null)}
              type="button"
            >
              <FormattedMessage id="SelectSingleFilter.popupClear" />
            </button>
          </MenuItem>
        </MenuContent>
      </Menu>
    );
  }
}

AgeRangeFilterPopup.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
};

AgeRangeFilterPopup.propTypes = {
  rootClassName: string,
  className: string,
  label: string.isRequired,
  queryParamNames: object.isRequired,
  onSubmit: func.isRequired,
  initialValues: object,
};

export default AgeRangeFilterPopup;

